import { isFunction } from 'lodash';
import { useCallback, useMemo } from 'react';

import { IdentityDocumentType } from '@dreryk/egabinet-patient-published-language-api';

import { assertUnreachable, Bean, BindingCreator, useTranslate, ValidationRules } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { identityDocumentTranslationKeys } from '../translations';

export type IdentityDocumentSelectType =
   | 'None'
   | 'IdentityCard'
   | 'Passport'
   | 'SeamansBook'
   | 'DrivingLicense'
   | 'StudentCard';
const IDENTITY_DOCUMENT_SELECT_TYPES: IdentityDocumentSelectType[] = [
   'None',
   'IdentityCard',
   'Passport',
   'SeamansBook',
   'DrivingLicense',
   'StudentCard',
];

export const getIdentityDocumentSelectType = (
   identityDocumentType: IdentityDocumentType | undefined
): IdentityDocumentSelectType => {
   switch (identityDocumentType) {
      case undefined:
         return 'None';
      case 'IDENTITY_CARD':
         return 'IdentityCard';
      case 'PASSPORT':
         return 'Passport';
      case 'SEAMANS_BOOK':
         return 'SeamansBook';
      case 'DRIVING_LICENCE':
         return 'DrivingLicense';
      case 'STUDENT_CARD':
         return 'StudentCard';
      default:
         return assertUnreachable(identityDocumentType);
   }
};

export const getIdentityDocumentType = (
   identityDocumentSelectType: IdentityDocumentSelectType | undefined
): IdentityDocumentType | undefined => {
   switch (identityDocumentSelectType) {
      case 'None':
         return undefined;
      case 'IdentityCard':
         return 'IDENTITY_CARD';
      case 'Passport':
         return 'PASSPORT';
      case 'SeamansBook':
         return 'SEAMANS_BOOK';
      case 'DrivingLicense':
         return 'DRIVING_LICENCE';
      case 'StudentCard':
         return 'STUDENT_CARD';
      default:
         return assertUnreachable(identityDocumentSelectType);
   }
};

export type IdentityDocumentSelectTypeVisitor = <ACCEPT>(
   visitor: ((type: IdentityDocumentSelectType) => ACCEPT) | Record<`visit${IdentityDocumentSelectType}`, () => ACCEPT>
) => ACCEPT;

export const createIdentityDocumentSelectTypeVisitor = (
   identityDocumentSelectType: IdentityDocumentSelectType
): IdentityDocumentSelectTypeVisitor => (visitor) => {
   if (isFunction(visitor)) {
      return visitor(identityDocumentSelectType);
   }

   switch (identityDocumentSelectType) {
      case 'None':
         return visitor.visitNone();
      case 'IdentityCard':
         return visitor.visitIdentityCard();
      case 'Passport':
         return visitor.visitPassport();
      case 'SeamansBook':
         return visitor.visitSeamansBook();
      case 'DrivingLicense':
         return visitor.visitDrivingLicense();
      case 'StudentCard':
         return visitor.visitStudentCard();
      default:
         return assertUnreachable(identityDocumentSelectType);
   }
};

const useTriggerLabelExtractor = (): ((type?: IdentityDocumentSelectType | null) => string) => {
   const translate = useTranslate();
   return useCallback(
      (type) => {
         return type ? translate(identityDocumentTranslationKeys.identityDocumentType.typeName[type]) : '';
      },
      [translate]
   );
};

export type OnIdentityDocumentTypeSelected = (type: IdentityDocumentSelectType | null) => void;

type IdentityDocumentTypeFieldProps = {
   readonly name: string;
   readonly register: BindingCreator<Bean>;
   readonly onSelect: OnIdentityDocumentTypeSelected;
};

export const IdentityDocumentTypeField = (props: IdentityDocumentTypeFieldProps): JSX.Element => {
   const validationRules = useMemo<ValidationRules<Bean>>(() => {
      return {
         required: true,
      };
   }, []);

   return (
      <Select<IdentityDocumentSelectType>
         testId={'identityDocumentType'}
         label={identityDocumentTranslationKeys.identityDocumentType.label}
         triggerLabelExtractor={useTriggerLabelExtractor()}
         controller={props.register(props.name, validationRules)}
         defaultValue={'None'}
         onSelect={props.onSelect}
         maxHeight={190}
      >
         <Select.List<IdentityDocumentSelectType> items={IDENTITY_DOCUMENT_SELECT_TYPES}>
            {(documentType: IdentityDocumentSelectType) => (
               <Select.Item
                  item={documentType}
                  testId={documentType}
                  label={identityDocumentTranslationKeys.identityDocumentType.typeName[documentType]}
               />
            )}
         </Select.List>
      </Select>
   );
};
